import { useTranslation } from 'next-i18next'
import { useEffect, useMemo, useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'

import { useRouter } from 'next/router'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Divider from '~/bootcamp/Activities/components/Divider'
import Equals from '~/bootcamp/Activities/components/Equals'
import InputSingleDigitColumns, {
  InputSingleDigitColumnsAnswer,
} from '~/bootcamp/Activities/components/InputSingleDigitColumns'
import MultiplicationActivityWrapper from '~/bootcamp/Activities/Multiplication/components/MultiplicationActivityWrapper'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { randomIntFromInterval, hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'
import { range } from '~/utils/helpers'

import useFocus from '~/bootcamp/Activities/hooks/useFocus'

import { getRanges2 } from './constants'
import { generateNumber } from './helpers'
import { TaskHistory } from './types'
import InputRowWrapper from './components/InputRowWrapper'
import Expression from './components/Expression'
import CarriedNumbersExpression from './components/CarriedNumbersExpression'

export default function LongMulti2({
  answerState,
  onAnswerStateChange,
  difficultyLevel,
  onNextQuestionClick,
  onInstanceChange,
  actionButtonsFC,
  autofocus = true,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation('generics')
  const { locale } = useRouter()
  const [answer, setAnswer] = useState<InputSingleDigitColumnsAnswer>({})
  const [inputKey, setInputKey] = useState<string>(uuid())
  const [carriedNumbers, setCarriedNumbers] = useState<Record<string, string>>({})
  const [taskHistory, setTaskHistory] = useState<TaskHistory[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  const { multiplierDigitMin, multiplierDigitMax, multiplierLen, multiplicandDigitMin, multiplicandDigitMax } =
    useMemo(() => {
      return getRanges2('LONG_MULTI_2', difficultyLevel)
    }, [difficultyLevel])

  const [multiplier, multiplicand, maxAnswerLen] = useMemo(() => {
    const taskHistoryMultipliers = taskHistory.map((elem) => elem.multiplier)
    const taskHistoryMultiplicands = taskHistory.map((elem) => elem.multiplicand)

    const _multiplier = generateNumber(multiplierDigitMin, multiplierDigitMax, multiplierLen, taskHistoryMultipliers)
    const _multiplicand = randomIntFromInterval(multiplicandDigitMin, multiplicandDigitMax, taskHistoryMultiplicands)
    const _maxAnswerLen = multiplierLen + 1

    setAnswer(generateInitialState(_maxAnswerLen))
    setCarriedNumbers(generateCarriedNumberInitialState(String(_multiplier).length - 1))

    return [String(_multiplier), String(_multiplicand), _maxAnswerLen]
  }, [multiplicandDigitMax, multiplicandDigitMin, multiplierDigitMax, multiplierDigitMin, multiplierLen, taskHistory])

  // Only the two factors — the rest of numbersState is the worked answer.
  useEmitInstance(
    onInstanceChange,
    multiplier && multiplicand ? `${multiplier} \\cdot ${multiplicand} = \\square` : null,
  )

  useEffect(() => {
    onAnswerStateChange(null)
  }, [answer, onAnswerStateChange])

  const handleOnCheckAnswerClick = (): void => {
    const trueAnswer = Number(multiplier) * Number(multiplicand)
    const enteredNumber = Number(Object.values(answer).join(''))
    const isCorrect = trueAnswer === enteredNumber
    onAnswerStateChange(isCorrect)
  }

  const handleOnNextQuestionClick = (): void => {
    setTaskHistory((prevState) => {
      return [{ multiplier: Number(multiplier), multiplicand: Number(multiplicand) }, ...prevState].slice(0, 3)
    })

    onAnswerStateChange(null)
    onNextQuestionClick()
    setInputKey(uuid())
  }

  const handleOnRemoveButtonClick = (): void => {
    setAnswer(generateInitialState(maxAnswerLen))
    setCarriedNumbers(generateCarriedNumberInitialState(multiplier.length - 1))
  }

  const handleOnAnswerChange = (value: string, name: string): void => {
    setAnswer((prevState) => ({ ...prevState, [name]: value }))
  }

  const handleOnCarriedNumbersChange = (value: string, name: string): void => {
    setCarriedNumbers((prevState) => ({ ...prevState, [name]: value }))
  }

  const onEnterPress = () => {
    if (isFormValid()) {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
    }
  }

  const commonGridStyle = {
    display: 'grid',
    gridTemplateColumns: `repeat(${maxAnswerLen + 1}, minmax(0, 1fr))`,
  }

  const inputGridStyle = {
    gridColumnStart: 2,
    gridColumnEnd: maxAnswerLen + 2,
  }

  const isFormValid = (): boolean => {
    return Object.keys(answer).some((key: string) => answer[key] !== '')
  }

  return (
    <ActivityWrapper>
      <MultiplicationActivityWrapper className="space-y-1 flex-col">
        <CarriedNumbersExpression
          carriedNumbers={carriedNumbers}
          gridCols={locale === 'no' ? maxAnswerLen + 1 : multiplier.length}
          onCarriedNumbersChange={handleOnCarriedNumbersChange}
        />
        <Expression maxAnswerLen={maxAnswerLen} multiplicand={multiplicand} multiplier={multiplier} />
        <Divider />
        <InputRowWrapper className="justify-between">
          <Equals />
          <div style={commonGridStyle}>
            <InputSingleDigitColumns
              aria-label={t('ariaLabelButtons.insertProduct')}
              autoFocus
              id={`LongMulti2:${inputKey}`}
              isError={answerState === false}
              onChange={handleOnAnswerChange}
              onEnter={onEnterPress}
              ref={inputRef}
              style={inputGridStyle}
              values={answer}
            />
          </div>
        </InputRowWrapper>
        <Divider />
        <Divider />
      </MultiplicationActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid: isFormValid(),
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: handleOnRemoveButtonClick,
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}

function generateInitialState(digitsCount: number): Record<string, string> {
  return range(digitsCount).reduce(
    (acc, currValue) => ({
      ...acc,
      [`digit-${currValue + 1}`]: '',
    }),
    {},
  )
}

function generateCarriedNumberInitialState(digitsCount: number): Record<string, string> {
  return range(digitsCount).reduce(
    (acc, currValue) => ({
      ...acc,
      [`number-${currValue + 1}`]: '',
    }),
    {},
  )
}
